/* eslint-disable react-refresh/only-export-components -- `quantidadeEmPalavras`
   é a mesma frase que o cabeçalho e a gaveta anunciam ao leitor de tela. Mora
   junto do botão de propósito: duplicá-la noutro arquivo é como as duas versões
   começam a divergir. O custo é recarregar este módulo inteiro no modo de
   desenvolvimento, o que é irrelevante num botão. */
import { forwardRef } from 'react';
import { ShoppingCart } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useCart } from '@/features/cart/CartContext';

export type CartButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

/**
 * Números por extenso até dez.
 *
 * Leitor de tela lê "3" de formas diferentes conforme o idioma configurado no
 * sistema, que nem sempre é português. Por extenso, o rótulo é lido certo em
 * qualquer configuração — e o contador do carrinho quase nunca passa de dez.
 */
const POR_EXTENSO = [
  'zero',
  'um',
  'dois',
  'três',
  'quatro',
  'cinco',
  'seis',
  'sete',
  'oito',
  'nove',
  'dez',
] as const;

export function quantidadeEmPalavras(quantidade: number): string {
  if (quantidade <= 0) return 'Carrinho vazio';
  if (quantidade === 1) return 'Carrinho com um item';
  const numero = POR_EXTENSO[quantidade] ?? String(quantidade);
  return `Carrinho com ${numero} itens`;
}

/**
 * Botão do carrinho para o cabeçalho — F4.3.4.
 *
 * O contador visual é `aria-hidden`: quem usa leitor de tela ouve o rótulo do
 * botão, que já diz a quantidade por extenso. Sem isso o número seria anunciado
 * duas vezes, uma delas fora de contexto.
 */
const CartButton = forwardRef<HTMLButtonElement, CartButtonProps>(
  ({ className, ...props }, ref) => {
    const { quantidadeTotal } = useCart();
    const temItens = quantidadeTotal > 0;

    return (
      <button
        ref={ref}
        type="button"
        aria-label={quantidadeEmPalavras(quantidadeTotal)}
        className={cn(
          'relative inline-flex h-11 w-11 items-center justify-center rounded-md text-santa-dark transition-colors',
          'hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-santa-green focus-visible:ring-offset-2',
          className,
        )}
        {...props}
      >
        <ShoppingCart className="h-5 w-5" aria-hidden="true" />

        {temItens ? (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-santa-green px-1 text-xs font-semibold text-white tabular-nums"
          >
            {quantidadeTotal > 99 ? '99+' : quantidadeTotal}
          </span>
        ) : null}
      </button>
    );
  },
);

CartButton.displayName = 'CartButton';

export { CartButton };
